import React, { useEffect, useState, useRef, useCallback } from 'react'
import { useAppData } from '../../context/appContext'
import { IonButton, IonInput, IonRow, IonCol, IonItem, IonSpinner, IonIcon, IonLabel } from '@ionic/react';
import { searchOutline } from 'ionicons/icons';
import "./messages.css"
import moment from 'moment';
//firebase
import { storage, firebaseAuthReady } from '../../config/firebase';
import { ref, getDownloadURL, uploadBytesResumable, deleteObject } from "firebase/storage"
import { MdOutlineAttachEmail } from "react-icons/md"
import { useContext } from 'react';
import AppContext from '../../components/AppContext';

import { getHistoricalData } from '../../util/helpers/HistoricalData';
import { BehaviorSubject } from 'rxjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFilePdf } from '@fortawesome/free-solid-svg-icons'
import { faFileImage } from '@fortawesome/free-solid-svg-icons'
import { close } from 'ionicons/icons'
import { formatAppDateTime } from '../../utils/dateFormat';

const BATCH_INTERVAL = 3000; // merge and display messages every 3s during SSE
const MESSAGES_PER_PAGE = 50; // initial display limit; load more on demand
let fileUploaded = new Map(); //maintain file name and download url
const Messages = ({ selectedPatientSubject }) => {

    const appData = useAppData()
    const allPatients = appData.allPatientData || []
    // The subject is picked by the top-level Subject dropdown in dashboard.jsx,
    // which stores the selection on appUserData in AppContext.  We derive the
    // current subject from there and fall back to the first available patient
    // so the messages view always has a selection to display.
    const selectedPatient =
        appData.appUserData?.attributes?.subjectId ||
        allPatients[0]?.attributes?.subjectId ||
        null
    const [messageArray, setMessageArray] = useState([])
    const [plaintext, setPlainText] = useState(null)
    const [uploadPercent, setUploadPercent] = useState(0)
    const [fileList, setFileList] = useState([])
    const [isLoaded,setIsLoaded] = useState(false)
    const [isMsgAvailable,setIsMsgAvailable] = useState(true)
    const [isLoadingMessages, setIsLoadingMessages] = useState(false)
    const [searchQuery, setSearchQuery] = useState('') // SRS-WEB-055: Message search
    const [filterRole, setFilterRole] = useState('all') // SRS-WEB-055: Message filter
    const [displayLimit, setDisplayLimit] = useState(MESSAGES_PER_PAGE)
    const [streamsActive, setStreamsActive] = useState(false) // true while SSE streams are running
    
    const {dtalService,userData} = useContext(AppContext)

    // Use useRef for message maps and subjects like the patient app
    const patientRxMessages = useRef(new Map())
    const clinicianRxMessages = useRef(new Map())
    const clinicianStreamControlSubject = useRef(new BehaviorSubject(false))
    const patientStreamControlSubject = useRef(new BehaviorSubject(false))
    const historicalClinicianMsgSubscription = useRef(undefined)
    const historicalPatientMsgSubscription = useRef(undefined)
    const clinicianStreamControlSubscription = useRef(undefined)
    const patientStreamControlSubscription = useRef(undefined)
    const isComponentMounted = useRef(true)
    const streamsCompletedRef = useRef(0) // track how many streams finished
    const batchTimerRef = useRef(null)
    const messagesEndRef = useRef(null)
    const pendingScrollRef = useRef(false)
    const allowedImageType = ['image/jpg', 'image/jpeg', 'image/png']

    // Merge, dedup, sort, and display messages from both streams
    const mergeAndDisplayMessages = useCallback(() => {
        if (!isComponentMounted.current) return;

        let allMessages = [];

        // Process messages from clinician channel (message:SubjectXXX)
        Array.from(patientRxMessages.current.values()).forEach(msg => {
            if (msg.features && msg.features.content && msg.features.content.properties) {
                const senderId = msg.features.content.properties.senderId;
                let role = "unknown";
                if (senderId === userData.userId) {
                    role = "clinician";
                } else if (senderId && senderId !== userData.userId) {
                    role = "patient";
                } else if (!senderId) {
                    role = "patient";
                }
                allMessages.push({
                    ...msg,
                    timestamp: moment(msg._modified).unix(),
                    role: role
                });
            }
        });

        // Process messages from patient channel (message:SubjectXXX-patient)
        Array.from(clinicianRxMessages.current.values()).forEach(msg => {
            if (msg.features && msg.features.content && msg.features.content.properties) {
                const senderId = msg.features.content.properties.senderId;
                let role = "patient";
                if (senderId === userData.userId) {
                    role = "clinician";
                }
                allMessages.push({
                    ...msg,
                    timestamp: moment(msg._modified).unix(),
                    role: role
                });
            }
        });

        // Deduplicate messages by modified timestamp + plaintext
        const uniqueMap = new Map();
        allMessages.forEach(msg => {
            const keyPartTime = (msg._modified || "");
            const keyPartText = (msg.features?.content?.properties?.plaintext || "");
            const key = `${keyPartTime}|${keyPartText}`;
            if (!uniqueMap.has(key)) {
                uniqueMap.set(key, msg);
            }
        });

        const dedupedMessages = Array.from(uniqueMap.values());

        // Sort messages by timestamp
        const sortedMessages = dedupedMessages.sort((a, b) => {
            const timestamp1 = a.timestamp || 0;
            const timestamp2 = b.timestamp || 0;
            return timestamp1 - timestamp2;
        });

        console.log("[Messages] Final sorted messages:", sortedMessages.length);

        setMessageArray(sortedMessages);
        setIsLoaded(true);
        setIsLoadingMessages(false);
        setIsMsgAvailable(sortedMessages.length > 0);
    }, [userData?.userId])

    const getMessage = useCallback(async (subjectId) => {
        if (!subjectId || !dtalService || !subjectId.length) {
            console.warn("[Messages] Missing required data for message fetch");
            return;
        }

        console.log("[Messages] Starting message fetch for subject:", subjectId);

        // Reset state before starting new fetch
        patientRxMessages.current.clear();
        clinicianRxMessages.current.clear();
        streamsCompletedRef.current = 0;
        // Create fresh BehaviorSubjects so old getHistoricalData timers
        // fire on abandoned subjects that nobody listens to
        clinicianStreamControlSubject.current = new BehaviorSubject(false);
        patientStreamControlSubject.current = new BehaviorSubject(false);

        // Clean up existing subscriptions
        if (clinicianStreamControlSubscription.current) {
            clinicianStreamControlSubscription.current.unsubscribe();
        }
        if (patientStreamControlSubscription.current) {
            patientStreamControlSubscription.current.unsubscribe();
        }
        // Close any previous SSE connections
        dtalService.unsubscribe('history-clinician').then(() => {});
        dtalService.unsubscribe('history-patient').then(() => {});

        // Reset UI state
        setMessageArray([]);
        setIsLoaded(false);
        setIsMsgAvailable(true);
        setIsLoadingMessages(true);
        setDisplayLimit(MESSAGES_PER_PAGE);
        setStreamsActive(true);
        pendingScrollRef.current = true; // scroll to bottom when first messages arrive

        // Clear previous batch timer
        if (batchTimerRef.current) { clearInterval(batchTimerRef.current); batchTimerRef.current = null; }

        const onStreamDone = () => {
            streamsCompletedRef.current += 1;
            if (streamsCompletedRef.current >= 2) {
                // Both streams complete — clear batch timer, final merge
                if (batchTimerRef.current) { clearInterval(batchTimerRef.current); batchTimerRef.current = null; }
                setStreamsActive(false);
                mergeAndDisplayMessages();
            }
        };

        try {
            // Launch BOTH streams in parallel with unique stream keys
            console.log("[Messages] Launching clinician stream:", "message:" + subjectId);
            historicalClinicianMsgSubscription.current = getHistoricalData({
                dtalService,
                identifier: "message:" + subjectId,
                thingUpdates: patientRxMessages.current,
                duControlSubject: clinicianStreamControlSubject.current,
                streamKey: "history-clinician",
                inactivityPeriod: 3000,
            });

            console.log("[Messages] Launching patient stream:", "message:" + subjectId + "-patient");
            historicalPatientMsgSubscription.current = getHistoricalData({
                dtalService,
                identifier: "message:" + subjectId + "-patient",
                thingUpdates: clinicianRxMessages.current,
                duControlSubject: patientStreamControlSubject.current,
                streamKey: "history-patient",
                inactivityPeriod: 3000,
            });

            // Subscribe to clinician stream completion
            clinicianStreamControlSubscription.current = clinicianStreamControlSubject.current.subscribe(isDone => {
                if (!isComponentMounted.current) return;
                if (isDone) {
                    console.log("[Messages] Clinician stream completed, messages:", patientRxMessages.current.size);
                    clinicianStreamControlSubject.current.next(false);
                    onStreamDone();
                }
            });

            // Subscribe to patient stream completion
            patientStreamControlSubscription.current = patientStreamControlSubject.current.subscribe(isDone => {
                if (!isComponentMounted.current) return;
                if (isDone) {
                    console.log("[Messages] Patient stream completed, messages:", clinicianRxMessages.current.size);
                    patientStreamControlSubject.current.next(false);
                    onStreamDone();
                }
            });

            // Batch timer: merge and display messages every 3s during SSE
            batchTimerRef.current = setInterval(() => {
                if (!isComponentMounted.current) return;
                if (patientRxMessages.current.size > 0 || clinicianRxMessages.current.size > 0) {
                    mergeAndDisplayMessages();
                }
            }, BATCH_INTERVAL);

        } catch (error) {
            console.error("[Messages] Error in getMessage:", error);
            setIsLoaded(true);
            setIsMsgAvailable(false);
            setIsLoadingMessages(false);
        }
    }, [dtalService, userData?.userId, mergeAndDisplayMessages])

    // Reload messages whenever the selected subject changes (top dropdown).
    useEffect(() => {
        if (selectedPatient) {
            getMessage(selectedPatient);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPatient]);

    useEffect(() => {
        appData.setSelected("Messages")
        // Set component mounted flag
        isComponentMounted.current = true;

        return () => {
            // Clean up on unmount
            console.log("[Messages] Component unmounting, cleaning up subscriptions");
            isComponentMounted.current = false;
            if (batchTimerRef.current) { clearInterval(batchTimerRef.current); batchTimerRef.current = null; }

            // Clean up both historical SSE connections
            dtalService?.unsubscribe('history-clinician').then(() => {
                console.log("[Messages] Clinician history stream unsubscribed");
            });
            dtalService?.unsubscribe('history-patient').then(() => {
                console.log("[Messages] Patient history stream unsubscribed");
            });

            if (clinicianStreamControlSubscription.current) {
                clinicianStreamControlSubscription.current.unsubscribe();
                clinicianStreamControlSubscription.current = undefined;
            }

            if (patientStreamControlSubscription.current) {
                patientStreamControlSubscription.current.unsubscribe();
                patientStreamControlSubscription.current = undefined;
            }

            // Reset state
            setMessageArray([]);
            setIsLoaded(false);
            setIsMsgAvailable(true);
            setIsLoadingMessages(false);
        };
    }, [dtalService])

    // Auto-scroll to bottom when messages first load or after sending
    useEffect(() => {
        if (pendingScrollRef.current && messageArray.length > 0) {
            pendingScrollRef.current = false;
            setTimeout(() => {
                messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            }, 100);
        }
    }, [messageArray])

    const createMessage = async () => {
        let data = {
            features: {
                content: {
                    properties: {
                    plaintext: plaintext,
                        senderId: userData.userId // Use clinician's userId as senderId
                    }
                }
                }
            }
        
        if(fileUploaded.size) {
            data.features.content.properties.filelinks = Array.from(fileUploaded.values());
        }

        // Send message to the patient's message channel
        await dtalService.set(`message:${selectedPatient}`, data)

        // Add the message to UI immediately
        let sentMessage = {};
        sentMessage["timestamp"] = Date.now()/1000;
        sentMessage["role"] = "clinician";
        sentMessage._modified = new Date().toISOString();
        sentMessage.features = data.features;
        pendingScrollRef.current = true; // scroll to bottom after sending
        setMessageArray([...messageArray, sentMessage]);

        setPlainText(null); // clear the message text
        setFileList([]);
        fileUploaded.clear(); // clear uploaded files
    }


    const handleFileUpload = async (e) => {
        const ALLOWED_FILE_TYPES = [
            'image/png', 'image/jpeg', 'image/gif',
            'application/pdf',
            'text/plain', 'text/csv',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        ];
        const MAX_FILE_SIZE = 10 * 1024 * 1024;
        let files = Array.from(e.target.files);

        // FIX(TC-WEB-MSG-002): Firebase Storage returned 403 for unauthenticated
        // uploads.  Wait for the anonymous sign-in promise before kicking off
        // uploadBytesResumable so the request carries an auth token.
        await firebaseAuthReady;

        files.forEach((file) => {
        if (!ALLOWED_FILE_TYPES.includes(file.type)) {
            alert(`File type not allowed: ${file.name}`);
            return;
        }
        if (file.size > MAX_FILE_SIZE) {
            alert(`File too large (max 10 MB): ${file.name}`);
            return;
        }
        // FIX(TC-WEB-MSG-002): surface the selected filename as soon as the file
        // is picked, before the Firebase upload completes.  Previously the label
        // only appeared after getDownloadURL() resolved, which made the
        // attachment UX and attachment-send E2E coverage depend on Firebase
        // upload latency / availability.
        setFileList((prev) => (prev.includes(file.name) ? prev : [...prev, file.name]));
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const storageRef = ref(storage, `/files/${safeName}`);

        const uploadTask = uploadBytesResumable(storageRef, file);

        uploadTask.on(
            "state_changed",
            (snapshot) => {
                const percent = Math.round(
                    (snapshot.bytesTransferred / snapshot.totalBytes) * 100
                );

                // update progress
                setUploadPercent(percent);
            },
            (err) => {
                // Make the failure visible in the UI instead of swallowing it.
                // storage/unauthorized → Storage Rules rejected the write.
                // storage/unauthenticated → the client had no Firebase identity.
                console.error("Firebase upload failed:", err && err.code, err && err.message, err);
                alert(
                    `Attachment upload failed for ${file.name}: ` +
                    `${(err && err.code) || "unknown error"}. ` +
                    `Check Firebase Auth (Anonymous) and Storage Rules.`
                );
                setFileList((prev) => prev.filter((n) => n !== file.name));
            },
            () => {
                // download url
                getDownloadURL(uploadTask.snapshot.ref).then((url) => {
                    fileUploaded.set(file.name,url);
                });
            }
        );
        })
    }

    const handleFileDelete= (selectedFile) => {
      
        const storageRef = ref(storage,fileUploaded.get(selectedFile));
        deleteObject(storageRef).then(() => {
            fileUploaded.delete(selectedFile);
            setFileList(Array.from(fileUploaded.keys()));
          }).catch((error) => {
            // Failed, 
          }); 

    }

    return (
        <div className='messages_container' >
            {selectedPatient && <div className='messages_box_container' style={{ width: '100%' }}>
                {/* SRS-WEB-055: Message search + filter — kept outside the
                    scrollable conversation so they remain visible while the
                    user scrolls through history.  Compact single-row layout
                    using native inputs; the previous IonItem wrapper forced
                    a ~60px list-row height that dominated the viewport. */}
                <div
                    style={{
                        padding: '8px 12px',
                        borderBottom: '1px solid #ddd',
                        display: 'flex',
                        gap: '10px',
                        alignItems: 'center',
                        flexShrink: 0,
                        background: '#fafafa',
                    }}
                >
                    <div
                        style={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            background: '#fff',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            padding: '0 10px',
                            height: '32px',
                        }}
                    >
                        <IonIcon
                            icon={searchOutline}
                            style={{ fontSize: '16px', color: '#888', marginRight: '6px' }}
                        />
                        <input
                            data-testid="TMESSAGESEARCH"
                            placeholder="Search messages..."
                            value={searchQuery || ''}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            style={{
                                flex: 1,
                                border: 'none',
                                outline: 'none',
                                fontSize: '13px',
                                color: '#333',
                                background: 'transparent',
                                height: '100%',
                            }}
                        />
                    </div>
                    <select
                        data-testid="TMESSAGEFILTER"
                        value={filterRole}
                        onChange={(e) => setFilterRole(e.target.value)}
                        style={{
                            width: '140px',
                            height: '32px',
                            padding: '0 8px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            background: '#fff',
                            color: '#333',
                            fontSize: '13px',
                            cursor: 'pointer',
                            outline: 'none',
                        }}
                    >
                        <option value="all">All Messages</option>
                        <option value="clinician">From Me</option>
                        <option value="patient">From Patient</option>
                    </select>
                </div>

                <div className='messages_container_height'>
                    <div>
                    {!isLoaded && (
                        <div style={{display: 'flex', alignItems: 'center', padding: '20px'}}>
                            <IonSpinner name="crescent" />
                            <IonLabel style={{marginLeft: '10px'}}>Loading messages... please wait</IonLabel>
                        </div>
                    )}
{/*                     {<IonLoading message={"Fetching messages for " + selectedPatient} isOpen={!isLoaded} > </IonLoading>} */}
                    {!isMsgAvailable && (
                        <div style={{padding: '20px', textAlign: 'center', color: '#666'}}>
                            <IonLabel>No messages found for {selectedPatient}</IonLabel>
                            <div style={{fontSize: '14px', marginTop: '10px'}}>
                                Messages may not exist in this environment, or you may not have access to them.
                                <br />
                                Check browser console for debugging information.
                            </div>
                            <IonButton
                                size="small"
                                fill="outline"
                                onClick={() => {
                                    if (selectedPatient) {
                                        getMessage(selectedPatient);
                                    }
                                }}
                                style={{ marginTop: '10px', '--color': '#ffffff', color: '#ffffff' }}
                            >
                                <span style={{ color: '#ffffff' }}>Refresh Messages</span>
                            </IonButton>
                        </div>
                    )}
                    <div style={{ padding: '0 15px' }}>
                        {/* SRS-WEB-055: Filter and search messages */}
                        {(() => {
                            let filteredMessages = messageArray || [];

                            // Filter by role
                            if (filterRole !== 'all') {
                                filteredMessages = filteredMessages.filter(msg => msg.role === filterRole);
                            }

                            // Search in message content
                            if (searchQuery && searchQuery.trim()) {
                                const query = searchQuery.trim().toLowerCase();
                                filteredMessages = filteredMessages.filter(msg => {
                                    const text = msg.features?.content?.properties?.plaintext || '';
                                    const textLower = text.toLowerCase();
                                    return textLower.includes(query);
                                });
                            }

                            // Show "no messages" if filtered results are empty and filters are active
                            if (filteredMessages.length === 0 && (filterRole !== 'all' || (searchQuery && searchQuery.trim()))) {
                                return (
                                    <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                                        <IonLabel>No messages match your search criteria</IonLabel>
                                    </div>
                                );
                            }

                            // Pagination: show only the most recent displayLimit messages
                            const totalCount = filteredMessages.length;
                            const startIndex = Math.max(0, totalCount - displayLimit);
                            const visibleMessages = filteredMessages.slice(startIndex);
                            const hasOlderMessages = startIndex > 0;

                            return (
                                <>
                                    {/* Load older messages button */}
                                    {hasOlderMessages && (
                                        <div style={{ textAlign: 'center', padding: '10px 0' }}>
                                            <IonButton
                                                size="small"
                                                fill="outline"
                                                onClick={() => setDisplayLimit(prev => prev + MESSAGES_PER_PAGE)}
                                                style={{ '--color': '#25BDBD', fontSize: '12px' }}
                                            >
                                                <span style={{ color: '#ffffff' }}>Load older messages </span>
                                            </IonButton>
                                        </div>
                                    )}
                                    {/* Loading indicator while streams are active */}
                                    {streamsActive && isLoaded && (
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px', color: '#999' }}>
                                            <IonSpinner name="dots" style={{ width: '16px', height: '16px', marginRight: '6px' }} />
                                            <span style={{ fontSize: '12px' }}>Loading more messages...</span>
                                        </div>
                                    )}
                                    {visibleMessages.map((item, index) => {
                                return (
                                    <IonRow key={index} style={{ margin: '10px 0' }}>
                                        <IonCol size="8" offset={item.role === "patient" ? "4" : "0"}>
                                            <div 
                                                className={`message-bubble ${item.role}`}
                                                style={{
                                                    backgroundColor: item.role === "clinician" ? "#25BDBD" : "#9E9E9E",
                                                    borderRadius: "15px",
                                                    margin: "8px 0",
                                                    padding: "12px 16px",
                                                    color: "white",
                                                    wordWrap: "break-word",
                                                    maxWidth: "100%"
                                                }}
                                            >
                                                <div className="message-content">
                                                    <div 
                                                        style={{
                                                            color: "white",
                                                            fontSize: "14px",
                                                            lineHeight: "1.4",
                                                            marginBottom: "8px",
                                                            whiteSpace: "pre-wrap"
                                                        }}
                                                    >
                                                        {item.features?.content?.properties?.plaintext || ''}
                                                    </div>
                                                    <div 
                                                        style={{
                                                            color: "rgba(255, 255, 255, 0.7)",
                                                            fontSize: "11px",
                                                            textAlign: "right"
                                                        }}
                                                    >
                                                        {item.timestamp ? formatAppDateTime(item.timestamp * 1000) : 'No timestamp'}
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {/* File attachments */}
                                            {item.features?.content?.properties?.filelinks && (
                                                <div style={{ marginTop: '10px' }}>
                                                    {item.features.content.properties.filelinks.map((fileLink, fileIndex) => {
                                                        let splitPaths = fileLink.split('/');  
                                                        let lastPath = splitPaths[splitPaths.length-1].split('?');
                                                        let filename = lastPath[0];
                                                        let filetype = (filename.indexOf(".pdf") === -1) ? "png" : "pdf";

                                                        return (
                                                            <IonItem key={fileIndex} color="light" lines="none" style={{ marginTop: '5px' }}>
                                                                <FontAwesomeIcon icon={filetype === "png" ? faFileImage : faFilePdf} />
                                                                <IonLabel>
                                                                    <a href={fileLink} target="_blank" rel="noopener noreferrer" style={{ color: '#25BDBD', textDecoration: 'none' }}>
                                                                        {filename}
                                                                    </a>
                                                                </IonLabel>
                                                            </IonItem>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </IonCol>                                            
                                    </IonRow>
                                );
                            })}
                                    <div ref={messagesEndRef} />
                                </>
                            );
                        })()}
                    </div>
                    </div>
                </div>
{/*                 <div style={{ display: "flex" }}>
                    {renderImages()}
                </div> */}

                <div className="input-container">
                    <label htmlFor={isLoadingMessages ? undefined : "icon-button-file"} style={isLoadingMessages ? { pointerEvents: 'none', opacity: 0.5 } : undefined}>
                        <MdOutlineAttachEmail style={{ fontSize: "30px", color: "#4D4D4D", cursor: isLoadingMessages ? "not-allowed" : "pointer" }} />
                    </label>
                    <input 
                        data-testid="TMESSAGEIMAGE" 
                        onChange={handleFileUpload} 
                        style={{ visibility: "hidden", width: '0px' }} 
                        accept={allowedImageType.join(',')} 
                        multiple 
                        id="icon-button-file" 
                        type="file"
                        disabled={isLoadingMessages}
                    />

                    <div className="message-input">
                        <IonInput 
                            data-testid="TMESSAGETEXT" 
                            onIonChange={(e) => setPlainText(e.detail.value)}
                            placeholder={isLoadingMessages ? "Loading messages..." : "Type your message..."}
                            value={plaintext}
                            fill="clear"
                            disabled={isLoadingMessages}
                            style={{ 
                                '--padding-start': '15px',
                                '--padding-end': '15px'
                            }}
                        />
                    </div>
                    
                    <IonButton 
                        data-testid="TSENDMESSAGE" 
                        onClick={createMessage}
                        className="send-button"
                        disabled={!plaintext?.trim() || isLoadingMessages}
                    >
                        Send
                    </IonButton>
                </div>
                <div>
                <IonRow class="ion-justify-content-end">
                {fileList.map((selectedFile, index) => {
                    return(
                            <IonCol class='ion-no-margin ion-no-padding ion-padding-end' size="auto">
                                <IonItem color="light"  lines="none">
                                <IonLabel data-testid={`TFILENAME_${index}`}>{selectedFile}</IonLabel>
                                <IonButton size="small" fill="clear" onClick={(e) => handleFileDelete(selectedFile)}>
                                <IonIcon icon={close}></IonIcon>
                                </IonButton>
                                </IonItem>
                            </IonCol>    
                    )
                })}
                </IonRow>  
                </div> 
           
            </div>}

        </div>
    )
}

export default Messages
